// listen-repeat.ts — the TOEFL-2026-style "Listen and Repeat" drill, ENTIRELY on-device.
// The device TTS speaks a sentence; the user repeats it via device dictation; the score
// is a LOCAL text-similarity measure (word-level Levenshtein). NO AI call, NO billing,
// NO audio anywhere near a server — this whole file is free to use offline-after-TTS.

export type RepeatLevel = "easy" | "medium" | "hard";

export interface RepeatSentence {
  level: RepeatLevel;
  text: string;
}

/** Build-time sentence bank (ours — plain everyday English, graded by length/complexity). */
export const REPEAT_SENTENCES: RepeatSentence[] = [
  // easy — short, common patterns
  { level: "easy", text: "The library opens at nine in the morning." },
  { level: "easy", text: "She takes the bus to work every day." },
  { level: "easy", text: "Can you tell me where the station is?" },
  { level: "easy", text: "I usually have coffee with my breakfast." },
  { level: "easy", text: "The weather was lovely last weekend." },
  { level: "easy", text: "He forgot his umbrella on the train." },
  { level: "easy", text: "We are meeting at the cafe after class." },
  { level: "easy", text: "My brother works in a small bookshop." },
  { level: "easy", text: "The lecture starts in twenty minutes." },
  { level: "easy", text: "Please write your name at the top of the page." },
  // medium — longer, one clause boundary
  { level: "medium", text: "If it rains tomorrow, the picnic will be moved indoors." },
  { level: "medium", text: "The professor asked us to submit the essay by Friday." },
  { level: "medium", text: "Although the museum was crowded, we enjoyed the exhibition." },
  { level: "medium", text: "The new shopping centre is much bigger than the old one." },
  { level: "medium", text: "You should check the timetable before you leave the house." },
  { level: "medium", text: "Most students prefer studying in the library in the evening." },
  { level: "medium", text: "The restaurant on the corner serves excellent vegetarian food." },
  { level: "medium", text: "She has been learning the piano since she was seven years old." },
  { level: "medium", text: "The company announced that the meeting would be postponed." },
  { level: "medium", text: "Public transport in this city has improved a lot recently." },
  // hard — longer, denser grammar
  { level: "hard", text: "Had the committee reviewed the proposal earlier, the project would have started on time." },
  { level: "hard", text: "The researchers concluded that regular exercise significantly reduces everyday stress." },
  { level: "hard", text: "Despite numerous objections from residents, the council approved the construction plan." },
  { level: "hard", text: "The exhibition, which features artists from twelve countries, runs until the end of October." },
  { level: "hard", text: "Whoever arrives first should unlock the classroom and turn on the projector." },
  { level: "hard", text: "The government introduced several measures aimed at reducing traffic congestion downtown." },
  { level: "hard", text: "By the time the results were published, most applicants had already accepted other offers." },
  { level: "hard", text: "The documentary explores how coastal communities are adapting to rising sea levels." },
  { level: "hard", text: "Not only did the volunteers clean the beach, but they also planted trees along the shore." },
  { level: "hard", text: "The spokesperson declined to comment until the investigation had been completed." },
];

export const LEVEL_LABELS: Record<RepeatLevel, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
};

/** Pick a random sentence of a level, avoiding an immediate repeat of `avoid`. */
export function pickSentence(level: RepeatLevel, avoid?: string): RepeatSentence {
  const pool = REPEAT_SENTENCES.filter((s) => s.level === level && s.text !== avoid);
  const all = pool.length > 0 ? pool : REPEAT_SENTENCES.filter((s) => s.level === level);
  return all[Math.floor(Math.random() * all.length)];
}

/** Normalize into comparison words: lowercase, strip punctuation, collapse whitespace. */
export function toWords(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[^a-z0-9' ]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

/** Word-level Levenshtein distance. */
function wordDistance(a: string[], b: string[]): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i, ...Array<number>(n).fill(0)];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[n];
}

/** Similarity score 0..100 between the target sentence and what the dictation heard. */
export function repeatScore(target: string, heard: string): number {
  const a = toWords(target);
  const b = toWords(heard);
  if (a.length === 0) return 0;
  if (b.length === 0) return 0;
  const dist = wordDistance(a, b);
  const score = 100 * (1 - dist / Math.max(a.length, b.length));
  return Math.max(0, Math.min(100, Math.round(score)));
}

/** Per-word verdict on the TARGET sentence (matched words via LCS alignment) — for the
 *  "which words did I miss" highlight. Purely display; the score above is the measure. */
export function targetWordMatches(target: string, heard: string): { word: string; hit: boolean }[] {
  // One normalized token per DISPLAY word (multi-token normalizations are joined) so the
  // LCS hit indices map 1:1 back onto the rendered words.
  const display = target.split(/\s+/).filter(Boolean);
  const norm = display.map((w) => toWords(w).join(""));
  const a = norm.filter(Boolean);
  const b = toWords(heard);
  // LCS table
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const hits = new Set<number>();
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { hits.add(i); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) i++;
    else j++;
  }
  // Map back onto the DISPLAY words of the target (keep punctuation/casing for rendering).
  let k = 0;
  return display.map((word, di) => {
    if (!norm[di]) return { word, hit: true }; // pure punctuation token
    const hit = hits.has(k);
    k++;
    return { word, hit };
  });
}
